import type { Hex } from 'viem';

import { decodePositionRouterRevert } from './decodePositionRouterRevert';

// viem nests the revert data at `error.cause.data` (or deeper depending on
// wallet propagation). Walk the cause chain to find it, then decode so the
// PositionRouter_* custom errors surface as readable messages instead of hex.

const isHexData = (value: unknown): value is Hex =>
  typeof value === 'string' && value.startsWith('0x') && value.length >= 10;

const extractRevertData = (error: unknown): Hex | undefined => {
  let current: unknown = error;
  let depth = 0;
  while (current && depth < 8) {
    if (typeof current === 'object') {
      const maybeData = (current as { data?: unknown }).data;
      if (isHexData(maybeData)) {
        return maybeData;
      }
      current = (current as { cause?: unknown }).cause;
    } else {
      return undefined;
    }
    depth += 1;
  }
  return undefined;
};

// Returns the original error with the decoded message when decodable; the
// original is preserved as `cause` so downstream logging keeps the viem stack.
export const wrapPositionRouterError = (error: unknown): unknown => {
  const data = extractRevertData(error);
  if (!data) {
    return error;
  }
  const decoded = decodePositionRouterRevert(data);
  if (!decoded) {
    return error;
  }
  const wrapped = new Error(decoded.message);
  (wrapped as Error & { cause?: unknown; errorName?: string }).cause = error;
  (wrapped as Error & { cause?: unknown; errorName?: string }).errorName = decoded.errorName;
  return wrapped;
};
